"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeaturesService = void 0;
const ObjectHelper_1 = require("../helpers/ObjectHelper");
const UrlHelper_1 = require("../helpers/UrlHelper");
const Utils_1 = require("../helpers/Utils");
const FeatureFlags_1 = require("../models/FeatureFlags");
const lodash_1 = require("lodash");
const ServerParamsService_1 = require("./ServerParamsService");
class FeaturesService {
    getFeatureFlags() {
        if (!this.featureFlags) {
            this.featureFlags = this.computeFeatureFlags();
        }
        return this.featureFlags;
    }
    setConfigIDs(configIDs) {
        this.configIDs = configIDs;
    }
    updateFlagsWithEcs(rawEcsFlags) {
        const flags = this.getFeatureFlags();
        const ecsFlags = this.mapRawFlags(rawEcsFlags);
        this.featureFlags = ObjectHelper_1.default.mergeObjects({}, flags, ecsFlags);
        return this.featureFlags;
    }
    computeFeatureFlags() {
        const queryParams = UrlHelper_1.UrlHelper.getQueryParameters();
        const qspFlags = this.mapRawFlags(queryParams);
        const globalFlagsRaw = ServerParamsService_1.default.getServerParams().featureFlags;
        const globalFlags = this.mapRawFlags(globalFlagsRaw);
        return ObjectHelper_1.default.mergeObjects({}, FeatureFlags_1.defaultFeatureFlags, globalFlags, qspFlags);
    }
    // this method will save FF if is true or string with value true
    mapRawFlags(rawFlags) {
        const flags = {};
        const featureFlagNames = Object.keys(FeatureFlags_1.defaultFeatureFlags);
        featureFlagNames.forEach(flag => {
            const flagAlias = FeatureFlags_1.featureFlagAliases[flag];
            if (typeof rawFlags === "object") {
                let parsedValue;
                if (rawFlags[flag] !== undefined) {
                    parsedValue = this.parseFFValue(rawFlags[flag], flag);
                }
                else if (flagAlias && rawFlags[flagAlias] !== undefined) {
                    parsedValue = this.parseFFValue(rawFlags[flagAlias], flag);
                }
                // Only add to flags if validation succeeded (not undefined)
                if (parsedValue !== undefined) {
                    flags[flag] = parsedValue;
                }
            }
        });
        return flags;
    }
    /**
     * Parses and validates a feature flag value against its expected type.
     * Type is determined from explicit schema or inferred from the default value.
     * Returns undefined if the value doesn't match the expected type or constraints.
     * @param val - The raw value to parse (from URL params or server config)
     * @param flagName - The feature flag name for schema lookup
     * @returns The validated value matching the flag's expected type, or undefined if invalid
     */
    parseFFValue(val, flagName) {
        // Try to get explicit schema, otherwise infer from default value
        const schema = FeatureFlags_1.featureFlagSchema[flagName] || this.inferSchemaFromDefault(flagName);
        if (!schema) {
            return undefined;
        }
        switch (schema.type) {
            case "boolean":
                return this.parseBoolean(val);
            case "number":
                return this.parseNumber(val, schema.min, schema.max);
            case "string":
                return this.parseString(val);
            case "string[]":
                return this.parseStringArray(val);
            default:
                return undefined;
        }
    }
    /**
     * Infers the feature flag schema from its default value.
     * This allows most flags to avoid explicit schema definitions.
     * @param flagName - The feature flag name
     * @returns Inferred schema based on default value type, or undefined if cannot infer
     */
    inferSchemaFromDefault(flagName) {
        const defaultValue = FeatureFlags_1.defaultFeatureFlags[flagName];
        if (defaultValue === undefined) {
            // Cannot infer type from undefined - schema entry required
            return undefined;
        }
        if (typeof defaultValue === "boolean") {
            return { type: "boolean" };
        }
        if (typeof defaultValue === "number") {
            return { type: "number", min: 0 }; // Default min constraint for numbers
        }
        if (typeof defaultValue === "string") {
            return { type: "string" };
        }
        if (Array.isArray(defaultValue)) {
            return { type: "string[]" };
        }
        return undefined;
    }
    parseBoolean(val) {
        // Arrays and numbers are not valid boolean values
        if ((0, lodash_1.isArray)(val) || typeof val === "number") {
            return undefined;
        }
        // Already a boolean
        if (typeof val === "boolean") {
            return val;
        }
        // String values: accept "true", "false", "1", "0", or empty string
        if ((0, lodash_1.isString)(val)) {
            const normalized = val.toLowerCase().trim();
            if (normalized === "true" || normalized === "1") {
                return true;
            }
            if (normalized === "false" || normalized === "0" || normalized === "") {
                return false;
            }
        }
        return undefined;
    }
    parseNumber(val, min, max) {
        // Already a number - validate constraints
        if (typeof val === "number") {
            if (min !== undefined && val < min) {
                return undefined;
            }
            if (max !== undefined && val > max) {
                return undefined;
            }
            return val;
        }
        // Arrays and booleans are not valid number values
        if ((0, lodash_1.isArray)(val) || typeof val === "boolean") {
            return undefined;
        }
        const numericValue = Utils_1.Utils.parseNumber(val);
        // parseNumber returns false for invalid numbers, number for valid ones
        if (typeof numericValue !== "number") {
            return undefined;
        }
        // Validate against min/max constraints if provided
        if (min !== undefined && numericValue < min) {
            return undefined;
        }
        if (max !== undefined && numericValue > max) {
            return undefined;
        }
        return numericValue;
    }
    parseString(val) {
        // Arrays, booleans, and numbers are not valid string values
        if ((0, lodash_1.isArray)(val) || typeof val === "boolean" || typeof val === "number") {
            return undefined;
        }
        // Accept any string value (including empty string)
        if ((0, lodash_1.isString)(val)) {
            return val;
        }
        return undefined;
    }
    parseStringArray(val) {
        if ((0, lodash_1.isArray)(val)) {
            // Validate that all elements are strings
            if (val.every(item => (0, lodash_1.isString)(item))) {
                return val;
            }
        }
        return undefined;
    }
}
exports.FeaturesService = FeaturesService;
const featuresService = new FeaturesService();
exports.default = featuresService;
